import { RenderableTreeNode, renderers } from '@markdoc/markdoc'
import { marked } from 'marked'
import sanitize from 'sanitize-html'

import { extractProductPathFromUrl } from '~/portal/components/ConsumerSideNavigation/helpers'
import {
  API_TYPE,
  ApiContent,
  ApiContentDiff,
  ApiTocItem,
  DEFINITION_MARKDOWN_API_TYPES,
  DefinitionMarkdownApiType,
  DocumentContent,
  DocumentContentDiff,
  DocumentTocItem,
  PublishedTableOfContents,
  TableOfContentsItem,
  TableOfContentsItemDiff
} from '~/portal/types/content'
import { TemplateApiContent, TemplateContent, TemplateDocumentContent } from '~/portal/types/template'
import { CONTENT_TYPE, DEFINITION_MARKDOWN_TYPES, DefinitionMarkdownType } from '~/shared/types/content'

const META_DESCRIPTION_MAX_LENGTH = 160

const truncateAtWordBoundary = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text
  }
  const truncated = text.slice(0, maxLength).replace(/\s+\S*$/, '')
  return `${truncated}…`
}

// Best-effort plain-text preview of an HTML string, for use as an og:description/twitter:description.
export const toPlainTextExcerpt = (html: string): string | undefined => {
  const plainText = sanitize(html.replace(/<\/(p|div|h[1-6]|li|tr|br)>/gi, '</$1> '), { allowedTags: [], allowedAttributes: {} })
    .replace(/\s+/g, ' ')
    .trim()
  return plainText ? truncateAtWordBoundary(plainText, META_DESCRIPTION_MAX_LENGTH) : undefined
}

const isMarkdocTag = (node: unknown): boolean =>
  Boolean(node) && typeof node === 'object' && '$$mdtype' in (node as object)

// processMarkdown returns either a single tagged node or an array of them (strings included, for text nodes).
const isMarkdocRenderableTree = (content: unknown): content is RenderableTreeNode =>
  Array.isArray(content) ? content.some(isMarkdocTag) : isMarkdocTag(content)

// `content` here is whatever shape the _docs.$productname.$ loader produces: a raw HTML string for HTML
// pages, a Markdoc RenderableTree for MARKDOWN pages and every definition markdown type
// (see DEFINITION_MARKDOWN_TYPES — processMarkdown output, rendered client-side, never serialised to a
// string server-side), or a parsed OpenAPI/AsyncAPI spec object (with an `info.description`) for API content.
export const getContentDescription = (content: unknown): string | undefined => {
  if (typeof content === 'string') {
    return toPlainTextExcerpt(content)
  }

  if (isMarkdocRenderableTree(content)) {
    return toPlainTextExcerpt(renderers.html(content))
  }

  const specDescription = (content as { info?: { description?: string } })?.info?.description
  return specDescription ? toPlainTextExcerpt(marked(specDescription) as string) : undefined
}

export const isDocumentContentGeneric = <T>(item: T): boolean =>
  (item as any)?.content?.type === CONTENT_TYPE.HTML || (item as any)?.content?.type === CONTENT_TYPE.MARKDOWN

export const isDocumentContent = (item: TableOfContentsItem): item is DocumentContent =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isTemplateDocumentContent = (item: TemplateContent): item is TemplateDocumentContent =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isDocumentContentDiff = (item: TableOfContentsItemDiff): item is DocumentContentDiff =>
  item.content?.type === CONTENT_TYPE.HTML || item.content?.type === CONTENT_TYPE.MARKDOWN

export const isApiContentDiff = (item: TableOfContentsItemDiff): item is ApiContentDiff =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isApiContent = (item: TableOfContentsItem): item is ApiContent =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isTemplateApiContent = (item: TemplateContent): item is TemplateApiContent =>
  item.content?.type === CONTENT_TYPE.API_URL

export const isPublishedDocumentContent = (content?: PublishedTableOfContents): content is DocumentTocItem =>
  content?.type === CONTENT_TYPE.HTML || content?.type === CONTENT_TYPE.MARKDOWN

export const isPublishedApiUrlContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.API_URL

export const isPublishedGraphQlContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.GRAPHQL

export const isPublishedMcpContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.MCP

export const isPublishedA2aContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.A2A

export const isPublishedGrpcContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  content?.type === CONTENT_TYPE.GRPC

export const isDefinitionMarkdownType = (type?: CONTENT_TYPE): type is DefinitionMarkdownType =>
  DEFINITION_MARKDOWN_TYPES.includes(type as DefinitionMarkdownType)

export const isPublishedDefinitionMarkdownContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  isDefinitionMarkdownType(content?.type)

export const isDefinitionMarkdownApiType = (type?: API_TYPE): type is DefinitionMarkdownApiType =>
  DEFINITION_MARKDOWN_API_TYPES.includes(type as DefinitionMarkdownApiType)

export const isPublishedApiContent = (content?: PublishedTableOfContents): content is ApiTocItem =>
  isPublishedApiUrlContent(content) || isPublishedDefinitionMarkdownContent(content)

export interface TocItemCore {
  id: string,
  title: string,
  content: { type: CONTENT_TYPE }
  children?: TocItemCore[]
}

const findItemRecursively = <T extends TocItemCore = TocItemCore>(tocItem: T, id: string): T | undefined => {
  if (tocItem.id === id) {
    return tocItem
  }
  if (isDocumentContentGeneric(tocItem) && tocItem.children?.length) {
    return tocItem.children
      .map(child => findItemRecursively(child as T, id))
      .filter(Boolean)
      .pop()
  }
  return undefined
}

export const findItem = <T extends TocItemCore>(tocItems: T[], id: string): T | undefined => tocItems
  .map(tocItem => findItemRecursively(tocItem, id))
  .filter(Boolean)
  .pop()

const findPublishedItemRecursively = (tocItem: PublishedTableOfContents, propertyValue: string, property: string = 'path'): PublishedTableOfContents | undefined => {
  if (tocItem[property] === propertyValue) {
    return tocItem
  }
  if (isPublishedDocumentContent(tocItem) && tocItem.children?.length > 0) {
    return tocItem.children
      .map(child => findPublishedItemRecursively(child, propertyValue, property))
      .filter(Boolean)
      .pop()
  }
  return undefined
}

export const findItemByPath = (tocItems: PublishedTableOfContents[], path: string): PublishedTableOfContents | undefined => tocItems
  .map(tocItem => findPublishedItemRecursively(tocItem, path))
  .filter(Boolean)
  .pop()

export const findItemByTocId = (tocItems: PublishedTableOfContents[], contentId: string): PublishedTableOfContents | undefined => tocItems
  .map(tocItem => findPublishedItemRecursively(tocItem, contentId, 'tableOfContentsId'))
  .filter(Boolean)
  .pop()

const findTemplateItemRecursively = (tocItem, propertyValue: string, property: string = 'slug') => {
  if (tocItem[property] === propertyValue) {
    return tocItem
  }
  if (tocItem.content.type === CONTENT_TYPE.HTML && tocItem.children?.length > 0) {
    return tocItem.children
      .map(child => findTemplateItemRecursively(child, propertyValue, property))
      .filter(Boolean)
      .pop()
  }
  return undefined
}

export const findTemplateItemByPath = (tocTemplateItems, path: string) => tocTemplateItems.map(tocItem => findTemplateItemRecursively(tocItem, path))
  .filter(Boolean)
  .pop()

export const getConsumerContentSlug = ({
  templateId,
  productSlug,
  sectionSlug,
  contentSlug,
}: {
  templateId?: string,
  productSlug?: string,
  sectionSlug?: string,
  contentSlug?: string,
} = {}) => {
  if (!productSlug && !templateId) {
    return '/'
  }
  if (!sectionSlug || !contentSlug) {
    return `/${productSlug || templateId}`
  }
  return `/${productSlug || templateId}/${sectionSlug}/${contentSlug}`
}

export const getProviderContentSlug = ({
  productSlug,
  contentId,
}: {
  productSlug?: string,
  contentId?: string,
} = {}) => {
  if (!productSlug) {
    return '/sp-admin'
  }
  if (!contentId) {
    return `/sp-admin/products/${productSlug}`
  }
  return `/sp-admin/products/${productSlug}/edit/content/${contentId}`
}

export const getProviderTemplateContentSlug = ({
  templateId,
  contentId,
}: {
  templateId?: string,
  contentId?: string,
} = {}) => {
  if (!templateId) {
    return '/sp-admin'
  }
  if (!contentId) {
    return `/sp-admin/templates/${templateId}`
  }
  return `/sp-admin/templates/${templateId}/edit/content/${contentId}`
}

export const convertTOCRecursively = <T, S extends TocItemCore>(items: S[], itemModifier: (item: S) => T): T[] => items.map((item) => {
  if (isDocumentContentGeneric(item)) {
    return ({
      ...itemModifier(item),
      children: convertTOCRecursively(item.children as S[], itemModifier),
    })
  }
  return itemModifier(item)
})

export const haveActiveChild = <T extends TocItemCore>(items: Array<T>, currentItemId: string | undefined, activeItemId: string | undefined): boolean => {
  if (currentItemId === undefined || activeItemId === undefined) {
    return false
  }
  const currentItem = findItem<T>(items, currentItemId)
  const activeItem = currentItem && isDocumentContentGeneric(currentItem) && findItem(currentItem?.children || [], activeItemId)
  return Boolean(activeItem)
}

export const flattenTOC = (items: TableOfContentsItemDiff[]) => items.reduce((flat: TableOfContentsItemDiff[], item: TableOfContentsItemDiff) =>
  'children' in item ? flat.concat(item, flattenTOC(item.children)) : flat.concat(item),
  [] as TableOfContentsItemDiff[])

export const sanitizeId = (id: string) => id.replace(/[^a-zA-Z0-9-_]/g, '_')

export const getConsumerContentPath = (pathname: string, itemPath: string) => `/${pathname.split('/')[1]}/${itemPath}`

export const getTOCBreadcrumbPaths = (tableOfContents: PublishedTableOfContents[], currentPathname: string) => {
  const path: { title: string, path: string, id: string }[] = []
  const productPath = extractProductPathFromUrl(currentPathname)

  const traverse = (items) => {
    for (const item of items) {
      path.push({
        title: item.title,
        path: getConsumerContentPath(currentPathname, item.path),
        id: sanitizeId(`breadcrumb-${item.path}`)
      })
      if (item.path === productPath) {
        return true
      }
      if (item.children && traverse(item.children)) {
        return true
      }
      path.pop()
    }
    return false
  }

  traverse(tableOfContents)

  return path
}